import { Alert, Snackbar } from "@mui/material";
import { useContext, useState } from "react";
import SessionContext from "../layout/SessionContext";
import { ClubMode } from "./clubApi";
import JoinClubDialog from "./JoinClubDialog";
import RegisterClubDialog from "./RegisterClubDialog";
import useUserClubs from "./useUserClubs";

/**
 * "Join a club" (players) and "Register a new club" (managers) dialogs.
 * On success the club list is refreshed and the new club becomes the selected one.
 */
function useClubActions(mode: ClubMode, devPort: string) {
  const { userName, selectClubManually, betaMode } = useContext(SessionContext);
  const { state, reload } = useUserClubs(devPort, userName, mode);
  const [dialog, setDialog] = useState<"join" | "register" | null>(null);
  const [message, setMessage] = useState("");

  const ready = state.status === "ready" ? state : null;
  const knownClubIds = ready
    ? [...ready.clubs, ...ready.waitlistClubs].map((club) => club.value)
    : [];

  function onJoined(clubId: number, waitlist: boolean) {
    setDialog(null);
    reload();
    if (waitlist) {
      setMessage("Request sent. The club manager will approve your membership.");
    } else {
      selectClubManually(clubId);
      setMessage("You've joined the club!");
    }
  }

  function onRegistered() {
    setDialog(null);
    reload().then((next) => {
      if (next.status === "ready") {
        const created = next.clubs.find((club) => !knownClubIds.includes(club.value));
        if (created) {
          selectClubManually(created.value);
        }
      }
    });
    setMessage("Your club has been registered!");
  }

  const dialogs = ready ? (
    <>
      {mode === "player" && (
        <JoinClubDialog
          open={dialog === "join"}
          onClose={() => setDialog(null)}
          onJoined={onJoined}
          devPort={devPort}
          playerId={ready.userId}
          joinedClubIds={knownClubIds}
          betaMode={betaMode}
        />
      )}
      {mode === "manager" && (
        <RegisterClubDialog
          open={dialog === "register"}
          onClose={() => setDialog(null)}
          onRegistered={onRegistered}
          devPort={devPort}
          managerId={ready.userId}
        />
      )}
      <Snackbar open={!!message} autoHideDuration={4000} onClose={() => setMessage("")}>
        <Alert severity="success" onClose={() => setMessage("")}>
          {message}
        </Alert>
      </Snackbar>
    </>
  ) : null;

  return {
    openJoin: () => setDialog("join"),
    openRegister: () => setDialog("register"),
    dialogs,
  };
}

export default useClubActions;
